import { motion } from "framer-motion";
import { STATE_NODES, ROUTES, INDIA_PATH, MAP_VIEWBOX, CITY_NODES, LANES, CITY_HUBS, RAPID_ZONE } from "../../lib/supplierNetwork";

const MAP_MOTION = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-100px" }, transition: { duration: 0.7 } };
const HUB = { fill: "var(--chart-lime)" };
const POINT = { fill: "var(--chart-blue)" };
const BLUE_STROKE = { stroke: "var(--chart-blue)" };

const curve = ({ from, to }) => {
  const mx = (from.x + to.x) / 2, my = (from.y + to.y) / 2;
  const dx = to.x - from.x, dy = to.y - from.y;
  return `M${from.x} ${from.y} Q${mx - dy * 0.25} ${my + dx * 0.25} ${to.x} ${to.y}`;
};

function RouteLine({ route, active }) {
  const lit = active && (route.from.id === active || route.to.id === active);
  return (
    <path d={curve(route)} fill="none" style={BLUE_STROKE} strokeWidth={lit ? 0.6 : 0.25} opacity={lit ? 0.9 : 0.25} strokeDasharray="1.2 1.2" className="transition-all duration-300">
      <animate attributeName="stroke-dashoffset" from="4.8" to="0" dur="2s" repeatCount="indefinite" />
    </path>
  );
}

function Truck({ path, dur, begin }) {
  return (
    <g>
      <animateMotion dur={`${dur}s`} begin={`${begin}s`} repeatCount="indefinite" path={path} rotate="auto" />
      <g transform="scale(0.8)">
        <rect x="-2.1" y="-0.65" width="2.6" height="1.3" rx="0.2" style={POINT} />
        <rect x="0.7" y="-0.55" width="0.9" height="1.1" rx="0.25" style={{ fill: "var(--ib-text)" }} />
        <rect x="-1.9" y="-0.3" width="2.2" height="0.18" fill="white" opacity="0.5" />
      </g>
    </g>
  );
}

function Lane({ lane, active }) {
  const lit = active && (lane.from.id === active || lane.to.id === active);
  const d = curve(lane);
  const dur = 6 + (lane.idx % 5);
  return (
    <g data-testid={lane.key}>
      <path d={d} fill="none" style={BLUE_STROKE} strokeWidth={lit ? 0.5 : 0.22} opacity={lit ? 0.85 : 0.35} strokeLinecap="round" className="transition-all duration-300" />
      <Truck path={d} dur={dur} begin={-(lane.idx % 4) * 1.3} />
      {lane.idx % 2 === 0 && <Truck path={d} dur={dur} begin={-(dur / 2) - (lane.idx % 3)} />}
    </g>
  );
}

function City({ city }) {
  return (
    <g data-testid={`map-city-${city.id}`}>
      <circle cx={city.x} cy={city.y} r="0.75" fill="var(--ib-bg)" style={BLUE_STROKE} strokeWidth="0.35" />
      <text className="map-city-label font-manrope" x={city.x + 1.4} y={city.y + 0.6} fontSize="1.75" style={{ fill: "var(--ib-text-secondary)" }}>{city.name}</text>
    </g>
  );
}

function Node({ node, active, onHover }) {
  const isActive = active === node.id;
  const style = node.hub ? HUB : POINT;
  const r = node.sub ? 1.25 : node.hub ? 1.7 : 1.1;
  const labelX = node.side === "left" ? node.x - 3 : node.x + 3;
  return (
    <g onMouseEnter={() => onHover(node.id)} onMouseLeave={() => onHover(null)} className="cursor-pointer" data-testid={`map-node-${node.id}`}>
      <circle cx={node.x} cy={node.y} r={r * 2.4} style={style} opacity={isActive ? 0.25 : 0.12} />
      <circle cx={node.x} cy={node.y} r={r} fill="none" style={{ stroke: style.fill }} strokeWidth="0.25">
        <animate attributeName="r" from={r} to={r * 3.2} dur="2.4s" begin={`${node.delay}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" from="0.7" to="0" dur="2.4s" begin={`${node.delay}s`} repeatCount="indefinite" />
      </circle>
      <circle cx={node.x} cy={node.y} r={isActive ? r * 1.35 : r} style={style} stroke={node.hub ? "var(--hub-ring)" : "none"} strokeWidth="0.3" className="transition-all duration-200" />
      <circle cx={node.x} cy={node.y} r={r * 0.4} fill="var(--ib-bg)" />
      {(node.hub || isActive) && (
        <text x={labelX} y={node.y + 0.9 + (node.labelDy || 0)} textAnchor={node.side === "left" ? "end" : "start"} className="font-outfit" fontSize={node.sub ? 2.1 : 2.6} fontWeight="700" style={{ fill: "var(--ib-text)" }}>
          {node.city}
          {(!node.sub || isActive) && <tspan x={labelX} dy="2.9" fontSize="1.9" fontWeight="400" style={{ fill: "var(--ib-text-muted)" }}>{node.sub || `${node.suppliers} suppliers`}</tspan>}
        </text>
      )}
    </g>
  );
}

function RapidZone() {
  const { x, y, r, label } = RAPID_ZONE;
  return (
    <g data-testid="rapid-zone" pointerEvents="none">
      <circle cx={x} cy={y} r={r * 1.6} fill="url(#rapid-glow)" />
      <circle cx={x} cy={y} r={r} fill="none" style={{ stroke: "var(--rapid-zone)" }} strokeWidth="0.35" strokeDasharray="0.8 0.6" opacity="0.9">
        <animateTransform attributeName="transform" type="rotate" from={`0 ${x} ${y}`} to={`360 ${x} ${y}`} dur="30s" repeatCount="indefinite" />
      </circle>
      <circle cx={x} cy={y} r={r} fill="none" style={{ stroke: "var(--rapid-zone)" }} strokeWidth="0.3">
        <animate attributeName="r" from={r * 0.6} to={r * 1.5} dur="3s" repeatCount="indefinite" />
        <animate attributeName="opacity" from="0.8" to="0" dur="3s" repeatCount="indefinite" />
      </circle>
      <g transform={`translate(${x - r - 9.5} ${y - 3.4})`}>
        <rect x="-8" y="-1.7" width="16" height="3.2" rx="1.6" style={{ fill: "var(--rapid-zone)" }} />
        <text x="0" y="0.55" textAnchor="middle" fontSize="1.55" fontWeight="700" className="font-jetbrains" style={{ fill: "var(--ib-bg)" }}>{label}</text>
      </g>
    </g>
  );
}

function MapDefs() {
  return (
    <defs>
      <linearGradient id="india-fill" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" style={{ stopColor: "var(--map-b)" }} stopOpacity="0.32" />
        <stop offset="55%" style={{ stopColor: "var(--map-a)" }} stopOpacity="0.2" />
        <stop offset="100%" style={{ stopColor: "var(--map-a)" }} stopOpacity="0.08" />
      </linearGradient>
      <linearGradient id="india-stroke" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" style={{ stopColor: "var(--map-b)" }} />
        <stop offset="100%" style={{ stopColor: "var(--map-a)" }} />
      </linearGradient>
      <radialGradient id="rapid-glow">
        <stop offset="0%" style={{ stopColor: "var(--rapid-zone)" }} stopOpacity="0.45" />
        <stop offset="60%" style={{ stopColor: "var(--rapid-zone)" }} stopOpacity="0.15" />
        <stop offset="100%" style={{ stopColor: "var(--rapid-zone)" }} stopOpacity="0" />
      </radialGradient>
      <filter id="map-glow" x="-10%" y="-10%" width="120%" height="120%">
        <feGaussianBlur stdDeviation="1.2" />
      </filter>
    </defs>
  );
}

function Legend() {
  return (
    <div className="relative px-4 pb-4 -mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 font-jetbrains text-[10px] text-ib-text-muted">
      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--chart-lime)" }} />Major hub</span>
      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--chart-blue)" }} />Supply point</span>
      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full border-2" style={{ borderColor: "var(--chart-blue)" }} />City</span>
      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full border border-dashed" style={{ borderColor: "var(--rapid-zone)", background: "color-mix(in srgb, var(--rapid-zone) 25%, transparent)" }} />Rapid 60-min zone</span>
      <span className="flex items-center gap-1.5"><span className="w-3.5 h-2 rounded-sm" style={{ background: "var(--chart-blue)" }} />Truck in transit</span>
    </div>
  );
}

export function IndiaMap({ active, onHover }) {
  return (
    <motion.div {...MAP_MOTION} className="lg:col-span-3 relative rounded-2xl border border-ib-steel/20 bg-ib-surface overflow-hidden w-full map-dots" data-testid="india-map-container">
      <div className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full border border-ib-steel/20 bg-ib-bg/70 backdrop-blur-md">
        <span className="relative flex w-2 h-2"><span className="absolute inset-0 rounded-full bg-ib-blue animate-ping opacity-60" /><span className="relative w-2 h-2 rounded-full bg-ib-blue" /></span>
        <span className="font-jetbrains text-[10px] text-ib-text-secondary uppercase tracking-widest">Live supply network · {LANES.length} lanes active</span>
      </div>
      <div className="relative w-full" style={{ paddingBottom: "87%" }}>
        <svg viewBox={MAP_VIEWBOX} className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <MapDefs />
          <path d={INDIA_PATH} fill="none" stroke="url(#india-stroke)" strokeWidth="1.2" opacity="0.35" filter="url(#map-glow)" />
          <path d={INDIA_PATH} fill="url(#india-fill)" stroke="url(#india-stroke)" strokeWidth="0.35" strokeLinejoin="round" />
          <RapidZone />
          {ROUTES.map((r) => <RouteLine key={r.key} route={r} active={active} />)}
          {LANES.map((l) => <Lane key={l.key} lane={l} active={active} />)}
          {CITY_NODES.map((c) => <City key={c.id} city={c} />)}
          {CITY_HUBS.map((n) => <Node key={n.id} node={n} active={active} onHover={onHover} />)}
          {STATE_NODES.map((n) => <Node key={n.id} node={n} active={active} onHover={onHover} />)}
        </svg>
      </div>
      <Legend />
    </motion.div>
  );
}
